import { apiService } from './apiService';

export interface Employee {
  personId: string;
  personnel_no: string;
  scg_empid: string;
  name_prefix_th: string;
  first_name_th: string;
  last_name_th: string;
  name_prefix_en: string;
  first_name_en: string;
  last_name_en: string;
  nickname: string;
  position_id: string;
  position_th: string;
  primary_position: string;
  pl_group: string;
  org_id: string;
  section_th: string;
  department_th: string;
  division_th: string;
  company_th: string;
  email: string;
}

export interface EmployeeSearchResponse {
  employees: Employee[];
  total: number;
}

export interface User {
  id: string;
  email: string;
  name: string;
  first_name?: string;
  last_name?: string;
  azure_id: string;
  tenant_id?: string;
  roles: string[];
  permissions: string[];
  department?: string;
  job_title?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  last_login?: string;
}

export interface UserListResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

class UserService {
  /**
   * Get current user information from backend
   * The backend will validate the JWT token and return user data
   * If user doesn't exist, backend will auto-create from Azure Entra ID claims
   */
  async getCurrentUser(): Promise<User> {
    try {
      // Call the backend endpoint that will:
      // 1. Validate the JWT token
      // 2. Extract user info from token
      // 3. Auto-create user if not exists
      // 4. Return user data
      const response = await apiService.get<User>('/api/users/me');
      return response;
    } catch (error) {
      console.error('Failed to get current user:', error);
      throw error;
    }
  }

  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<User> {
    return apiService.get<User>(`/api/users/${userId}`);
  }

  /**
   * List all users with pagination
   */
  async listUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<UserListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    const endpoint = query ? `/api/users?${query}` : '/api/users';
    
    return apiService.get<UserListResponse>(endpoint);
  }

  /**
   * Update user information
   */
  async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    return apiService.put<User>(`/api/users/${userId}`, updates);
  }

  /**
   * Sync user from Azure Entra ID
   */
  async syncUserFromAzure(azureUserId: string): Promise<User> {
    return apiService.post<User>('/api/users/sync', {
      azure_user_id: azureUserId
    });
  }

  /**
   * Search employees by name or email
   */
  async searchEmployees(value: string): Promise<EmployeeSearchResponse> {
    return apiService.get<EmployeeSearchResponse>(`/api/users/employees/search/${encodeURIComponent(value)}`);
  }

  /**
   * Delete user
   */
  async deleteUser(userId: string): Promise<void> {
    return apiService.delete<void>(`/api/users/${userId}`);
  }
}

// Export singleton instance
export const userService = new UserService();